import React, { useState, useEffect } from 'react'
import { useContext } from 'react'
import { VStack, Text, Button, Img, Box } from '@chakra-ui/react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import EFeatureFlags from 'enums/featureFlags'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { FlagValue, useFlag } from 'react-unleash-flags'
import { ApplicationContext } from '../../../../applicationForm.context'
import { STRIPE_PUBLISHABLE_KEY } from '../../../../constant'
import { CompleteScreeningContext } from '../../completeScreening.context'
import CheckoutForm from '../CheckoutForm'

interface IPaymentInformationProps {
  amount: number
}

const PaymentInformation = ({ amount }: IPaymentInformationProps) => {
  const { paymentClientSecret: clientSecret } = useContext(ApplicationContext)
  const { nextStep } = useContext(CompleteScreeningContext)
  const stripePromise: Promise<Stripe | null> = loadStripe(STRIPE_PUBLISHABLE_KEY)

  const { houseApplicationStore } = useStores()
  const { isTestPhoneNumber } = houseApplicationStore
  const [isLoading, setIsLoading] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const paymentFeatureFlagName: string = `${process.env.REACT_APP_FLAGS_CTX_APP_NAME}-${EFeatureFlags.WEBSITE_APPLICATION_FORM_PAYMENT}`
  const paymentFeatureFlag: FlagValue | undefined = useFlag(paymentFeatureFlagName)

  function onClose(): void {
    setIsFailed(false)
  }

  useEffect(() => {
    if (paymentFeatureFlag && !paymentFeatureFlag?.enabled && isTestPhoneNumber) {
      nextStep()
    }
  }, [paymentFeatureFlag])

  return (
    <Box maxWidth={{ base: 'full', md: '736px', lg: '500px' }} marginX="auto" minHeight={{ base: 'full', lg: '100vh' }}>
      <VStack
        spacing={6}
        position="absolute"
        top="50%"
        transform="translate(-50%, -50%)"
        justifyContent="center"
        hidden={!isLoading}
      >
        <Img src="/assets/icons/loading_animated.svg" alt="loading" />
        <Text fontSize="md" fontWeight="500" color="gray.700">
          Loading, please wait.
        </Text>
      </VStack>
      <VStack
        position="absolute"
        top="50%"
        transform="translate(-50%, -50%)"
        justifyContent="center"
        hidden={!isFailed || isLoading}
        width="100%"
        paddingX={6}
        spacing={8}
      >
        <VStack spacing={{ base: 4, lg: 6 }}>
          <Img src="/assets/images/sad.png" alt="failed" />
          <Text
            width={{ base: '100%', md: '360px' }}
            textAlign="center"
            fontSize="md"
            fontWeight="500"
            color="gray.700"
          >
            Unfortunately, you didn’t meet the minimum credit score required to join Homeroom at this time.
          </Text>
        </VStack>
        <Button
          display={{ base: 'none', lg: 'inline-flex' }}
          height={{ base: '48px', lg: '40px' }}
          marginTop={2}
          colorScheme="teal"
          color="white"
          boxShadow="base"
          variant="solid"
          minWidth="200px"
          onClick={onClose}
        >
          Close
        </Button>
      </VStack>
      <Box
        paddingX={4}
        paddingY={{ base: 6, lg: 8 }}
        height={{ base: 'full', md: 'unset' }}
        hidden={isLoading || isFailed || !clientSecret}
      >
        {clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: 'stripe' as 'stripe' }
            }}
          >
            <CheckoutForm isLoading={isLoading} amount={amount} setIsLoading={setIsLoading} setIsFailed={setIsFailed} />
          </Elements>
        )}
      </Box>
    </Box>
  )
}

export default observer(PaymentInformation)
